import { useCallback, useState } from 'react';

const FALLBACK_PERMISSION = 'unsupported';

export function useOwnerNotifications() {
  const [permission, setPermission] = useState(() => {
    if (typeof window === 'undefined' || !('Notification' in window)) {
      return FALLBACK_PERMISSION;
    }

    return Notification.permission;
  });

  const requestPermission = useCallback(async () => {
    if (typeof window === 'undefined' || !('Notification' in window)) {
      setPermission(FALLBACK_PERMISSION);
      return FALLBACK_PERMISSION;
    }

    const current = Notification.permission;
    if (current === 'default') {
      const result = await Notification.requestPermission();
      setPermission(result);
      return result;
    }

    setPermission(current);
    return current;
  }, []);

  const sendNotification = useCallback(
    async ({ title, body, tag }) => {
      if (typeof window === 'undefined' || !('Notification' in window)) {
        return false;
      }

      let currentPermission = permission;
      if (currentPermission === 'default') {
        currentPermission = await requestPermission();
      }

      if (currentPermission !== 'granted') {
        return false;
      }

      const notificationOptions = {
        body,
        tag,
        icon: '/icons/icon-192x192.png',
        badge: '/icons/icon-192x192.png',
        timestamp: Date.now(),
      };

      try {
        if ('serviceWorker' in navigator) {
          const registration = await navigator.serviceWorker.ready;
          await registration.showNotification(title, notificationOptions);
        } else {
          new Notification(title, notificationOptions);
        }
        return true;
      } catch (error) {
        console.error('Notification error', error);
        return false;
      }
    },
    [permission, requestPermission],
  );

  return {
    permission,
    requestPermission,
    sendNotification,
    supported: permission !== FALLBACK_PERMISSION,
  };
}
